<template>
  <div
    id="call_actions"
    class="flex flex-col proxima_nova font-medium w-[300px]"
    ref="callActionsRef"
  >
    <div class="flex justify-center">
      <div
        class="grid grid-cols-4 gap-2 justify-items-center"
      >
        <div
          v-for="action in visibleActions"
          :key="action.id"
          :id="`${action.id}_action`"
          class="flex flex-col gap-[6px] items-center"
          @click="
            isCallActive && !isActionDisabled(action.id)
              ? toggleAction(action.id)
              : null
          "
        >
          <div
            class="call_action_container flex justify-center items-center size-10 rounded-lg cursor-pointer gap-1"
            :class="{
              [action.style]: action.selected && isActionSelected(action.id),
              disabled: !isCallActive || isActionDisabled(action.id),
            }"
            :title="getActionDisabledTitle(action.id)"
          >
            <ThemeImage
              :class="`min-w-[${action?.size}px] min-h-[${action?.size}px] action-icon`"
              :icon="
                action.icon
                  ? action.icon
                  : isActionSelected(action.id)
                    ? action.selected
                    : action.unselected
              "
              :alt="`${action.id}_icon`"
            />
          </div>
          <span class="secondary_color text-[11px] text-center max-w-[52px] leading-tight">{{
            actionText(action)
          }}</span>
        </div>
      </div>
    </div>

    <div>
      <CustomDraggableModal
        :title="callActionModals[callActionsList.DIALER].title"
        v-model="showDialerModal"
        :width="callActionModals[callActionsList.DIALER].width"
        :show-overlay="false"
        :initial-position="modalPositions[callActionModals[callActionsList.DIALER].positionKey]"
        @update:model-value="handleDialerModalClose"
        @position-change="(pos) => updateModalPosition(callActionModals[callActionsList.DIALER].positionKey, pos)"
      >
        <Dialer source="call_actions" />
      </CustomDraggableModal>

      <CustomDraggableModal
        :title="callActionModals[callActionsList.NOTE].title"
        v-model="showNoteModal"
        :width="callActionModals[callActionsList.NOTE].width"
        :show-overlay="false"
        :initial-position="modalPositions[callActionModals[callActionsList.NOTE].positionKey]"
        @update:model-value="handleNoteModalClose"
        @position-change="(pos) => updateModalPosition(callActionModals[callActionsList.NOTE].positionKey, pos)"
      >
        <CallNote source="call_actions" v-model="noteAdded" />
      </CustomDraggableModal>

      <CustomDraggableModal
        :title="callActionModals[callActionsList.TRANSFER].title"
        v-model="showTransferCall"
        :width="callActionModals[callActionsList.TRANSFER].width"
        :show-overlay="false"
        :initial-position="modalPositions[callActionModals[callActionsList.TRANSFER].positionKey]"
        @update:model-value="handleTransferModalClose"
        @position-change="(pos) => updateModalPosition(callActionModals[callActionsList.TRANSFER].positionKey, pos)"
      >
        <TransferCall source="call_actions" />
      </CustomDraggableModal>

      <CustomDraggableModal
        :title="callActionModals[callActionsList.VOICEMAIL_DROP].title"
        v-model="showVoicemailDropModal"
        :width="callActionModals[callActionsList.VOICEMAIL_DROP].width"
        :show-overlay="false"
        :initial-position="modalPositions[callActionModals[callActionsList.VOICEMAIL_DROP].positionKey]"
        @update:model-value="handleVoicemailDropModalClose"
        @position-change="(pos) => updateModalPosition(callActionModals[callActionsList.VOICEMAIL_DROP].positionKey, pos)"
      >
        <VoicemailDrop
          @success="() => handleVoicemailDropModalClose(false)"
        />
      </CustomDraggableModal>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, watch, computed } from "vue";
import { message } from "ant-design-vue";
import { telephonyFeatures, telephonyProviders } from "@/src/constants/telephonyConstants.js";
import { callActions, callActionsList, callActionModals } from "@/src/constants/callConstants.js";
import { telephonyProviderGenericWrapper } from "@/src/constants/phoneNumberConstants.js";
import {
  getCurrentConnection,
  getIncomingConnection,
  toSwapCallsInConference,
  toMergeConference,
} from "@/src/utils/callsUtils.js";
import store from "@/src/store/index.js";
import eventBus from "@/src/utils/eventBus.js";
import CallService from "@/src/api/CallService.js";
import providerFeatureManager from "@/src/services/telephony/providers/providerFeatureManager.js";
import CustomDraggableModal from "@/src/components/CustomDraggableModal.vue";
import Dialer from "@/src/components/telephony_components/dialer/Dialer.vue";
import CallNote from "@/src/components/telephony_components/call_details/CallNote.vue";
import TransferCall from "@/src/components/telephony_components/actions/TransferCall.vue";
import VoicemailDrop from "@/src/components/telephony_components/actions/VoicemailDrop.vue";

const callActionsRef = ref(null);
const showDialerModal = ref(false);
const showNoteModal = ref(false);
const showTransferCall = ref(false);
const showVoicemailDropModal = ref(false);
const isMuted = ref(false);
const isOnHold = ref(false);
const isRecording = ref(false);
const noteAdded = ref("");
const isCallActive = ref(false);

const currentProvider = computed(
  () => store.getters["telephony/getCurrentProvider"],
);

const isGlobalRecordingEnabled = computed(
  () => store.getters["dialerSettings/isGlobalRecordingEnabled"],
);

const isWarmTransferEnabled = computed(
  () => store.getters["conferenceCall/isWarmTransfer"],
);

const swappingCalls = computed(
  () => store.getters["conferenceCall/swappingCalls"],
);

const isMerged = computed(() => store.getters["conferenceCall/isMerged"]);

const merging = computed(() => store.getters["conferenceCall/merging"]);

const transferTargetActive = computed(
  () => store.getters["conferenceCall/transferTargetActive"],
);

const isCampaignRecordingEnabled = computed(
  () => store.getters["callsCampaign/isRecordingEnabled"],
);

const isCampaignActive = computed(
  () => store.getters["callsCampaign/isCurrentCampaign"],
);

const actionFeatureMapping = {
  [callActionsList.DIALER]: telephonyFeatures.callDialer,
  [callActionsList.MUTE]: telephonyFeatures.callMute,
  [callActionsList.HOLD]: telephonyFeatures.callHold,
  [callActionsList.NOTE]: telephonyFeatures.callNote,
  [callActionsList.RECORD]: telephonyFeatures.callRecording,
  [callActionsList.TRANSFER]: telephonyFeatures.coldTransfer,
  [callActionsList.SWAP]: telephonyFeatures.callSwap,
  [callActionsList.MERGE]: telephonyFeatures.warmTransfer,
  [callActionsList.VOICEMAIL_DROP]: telephonyFeatures.voicemailDrop,
};

const actionVisibilityRules = computed(() => ({
  transfer: currentProvider.value === telephonyProviders.twilio && !isWarmTransferEnabled.value,
  swap:
    currentProvider.value === telephonyProviders.twilio &&
    isWarmTransferEnabled.value &&
    !isMerged.value,
  merge:
    currentProvider.value === telephonyProviders.twilio &&
    isWarmTransferEnabled.value &&
    !isMerged.value,
  voicemail_drop: currentProvider.value === telephonyProviders.twilio,
}));

const actionDisableConditions = computed(() => {
  const conditions = {
    [callActionsList.SWAP]: {
      condition:
        swappingCalls.value ||
        !store.getters["conferenceCall/transferTargetActive"],
      message: "Call swap in progress",
    },
    [callActionsList.MERGE]: {
      condition: merging.value || !transferTargetActive.value,
      message: merging.value
        ? "Merging in progress"
        : "Waiting for transfer target",
    },
  };

  Object.keys(actionFeatureMapping).forEach((actionId) => {
    const feature = actionFeatureMapping[actionId];
    if (currentProvider.value && feature) {
      const isAvailable = providerFeatureManager.isFeatureAvailable(
        currentProvider.value,
        feature,
      );
      if (!isAvailable) {
        conditions[actionId] = {
          condition: true,
          message: `${getFeatureName(actionId)} not available for ${currentProvider.value}`,
        };
      }
    }
  });

  return conditions;
});

const visibleActions = computed(() => {
  return callActions.filter((action) => {
    const rule = actionVisibilityRules.value[action.id];
    return rule === undefined ? true : rule;
  });
});

const getFeatureName = (actionId) => {
  const featureNames = {
    [callActionsList.DIALER]: "Dialer",
    [callActionsList.MUTE]: "Mute",
    [callActionsList.HOLD]: "Hold",
    [callActionsList.NOTE]: "Note",
    [callActionsList.RECORD]: "Recording",
    [callActionsList.TRANSFER]: "Transfer",
    [callActionsList.SWAP]: "Call Swap",
    [callActionsList.MERGE]: "Merge",
    [callActionsList.VOICEMAIL_DROP]: "Voicemail Drop",
  };
  return featureNames[actionId] || actionId;
};

const actionText = computed(
  () => (action) =>
    isActionSelected(action.id) && action.active ? action.active : action.text,
);

const isActionDisabled = (actionId) => {
  const disableRule = actionDisableConditions.value[actionId];
  return disableRule ? disableRule.condition : false;
};

const getActionDisabledTitle = (actionId) => {
  if (!isCallActive.value) return "Call not active";

  const disableRule = actionDisableConditions.value[actionId];
  if (disableRule && disableRule.condition) {
    return disableRule.message;
  }
  return "";
};

const getInitialPosition = () => {
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;
  return {
    x: screenWidth * 0.18,
    y: screenHeight * 0.28,
  };
};

const modalPositions = reactive({
  dialer: getInitialPosition(),
  note: getInitialPosition(),
  transfer: getInitialPosition(),
  voicemailDrop: getInitialPosition(),
});

const updateModalPosition = (modalType, position) => {
  modalPositions[modalType] = position;
};

const isActionSelected = (actionId) => {
  if (!isCallActive.value) return false;

  const action = callActions.find((a) => a.id === actionId);
  if (action && action.icon) return false;

  if (actionId === callActionsList.DIALER) return showDialerModal.value;
  if (actionId === callActionsList.MUTE) return isMuted.value;
  if (actionId === callActionsList.HOLD) return isOnHold.value;
  if (actionId === callActionsList.NOTE) return showNoteModal.value;
  if (actionId === callActionsList.RECORD) return isRecording.value;
  if (actionId === callActionsList.TRANSFER) return showTransferCall.value;
  if (actionId === callActionsList.VOICEMAIL_DROP) return showVoicemailDropModal.value;
  return false;
};

const resetAllStates = () => {
  showDialerModal.value = false;
  showNoteModal.value = false;
  showTransferCall.value = false;
  showVoicemailDropModal.value = false;
  isMuted.value = false;
  isOnHold.value = false;
  isRecording.value = false;
  noteAdded.value = "";
  isCallActive.value = false;
};

const toggleAction = async (actionId) => {
  if (!isCallActive.value) return;

  if (isActionDisabled(actionId)) {
    console.warn(
      `Action ${actionId} is disabled: ${getActionDisabledTitle(actionId)}`,
    );
    return;
  }

  switch (actionId) {
    case callActionsList.DIALER:
      showDialerModal.value = !showDialerModal.value;
      break;
    case callActionsList.MUTE:
      isMuted.value = !isMuted.value;
      const currentConnection = getCurrentConnection();
      const incomingConnection = getIncomingConnection();
      if (incomingConnection && incomingConnection.mute) {
        incomingConnection.mute(isMuted.value);
      }
      if (currentConnection && currentConnection.mute) {
        currentConnection.mute(isMuted.value);
      }
      break;
    case callActionsList.HOLD:
      isOnHold.value = !isOnHold.value;
      break;
    case callActionsList.NOTE:
      showNoteModal.value = !showNoteModal.value;
      break;
    case callActionsList.TRANSFER:
      showTransferCall.value = !showTransferCall.value;
      break;
    case callActionsList.VOICEMAIL_DROP:
      showVoicemailDropModal.value = !showVoicemailDropModal.value;
      break;
    case callActionsList.SWAP:
      await handleCallSwap();
      break;
    case callActionsList.MERGE:
      await handleCallMerge();
      break;
    case callActionsList.RECORD:
      if (store.getters["calls/recordingConsentStatus"]) {
        isRecording.value = !isRecording.value;
        await handleRecording();
        break;
      } else {
        eventBus.emit("open-consent-form", true);
      }
      break;
  }
};

const handleDialerModalClose = (value) => {
  showDialerModal.value = value;
};

const handleNoteModalClose = (value) => {
  showNoteModal.value = value;
};

const handleTransferModalClose = (value) => {
  showTransferCall.value = value;
};

const handleVoicemailDropModalClose = (value) => {
  showVoicemailDropModal.value = value;
};

const handleCallOpen = () => {
  resetAllStates();
  isCallActive.value = true;

  if (
    providerFeatureManager.isFeatureAvailable(
      currentProvider.value,
      telephonyFeatures.callRecording,
    )
  ) {
    const shouldRecord = isCampaignActive.value
      ? isCampaignRecordingEnabled.value
      : isGlobalRecordingEnabled.value;

    if (shouldRecord) {
      isRecording.value = true;
    }
  }
};

const handleRecording = async () => {
  const currentConnection = getCurrentConnection();
  const incomingConnection = getIncomingConnection();

  if (incomingConnection?.parameters?.CallSid) {
    await toInitiateCallRecording(incomingConnection.parameters.CallSid);
  }
  if (currentConnection?.parameters?.CallSid) {
    await toInitiateCallRecording(currentConnection.parameters.CallSid);
  }
};

const handleCallSwap = async () => {
  try {
    await toSwapCallsInConference();
  } catch (error) {
    console.error("An error occurred while handling call swap: ", error);
  }
};

const handleCallMerge = async () => {
  try {
    await toMergeConference();
  } catch (error) {
    console.error("An error occurred while handling call merge: ", error);
    message.error(
      error?.response?.data?.error || "Failed to merge conference",
    );
  }
};

const toInitiateCallRecording = async (ssid) => {
  try {
    const payload = {
      provider:
        telephonyProviderGenericWrapper[
          store.getters["telephony/getCurrentProvider"]?.toString()
        ],
    };

    const response = await CallService.initiateCallRecording(ssid, payload);
    if (response?.success) {
      console.log(response);
    }
  } catch (error) {
    message.error(
      error?.response?.data?.error ||
        "An error occurred while initiating call recording",
    );
    console.error(
      `An error occurred while initiating call recording: ${error}`,
    );
    isRecording.value = false;
  }
};

watch(isWarmTransferEnabled, (newVal) => {
  if (newVal) {
    showTransferCall.value = false;
  }
});

watch(
  () => store.getters["calls/recordingConsentStatus"],
  (newValue) => {
    if (
      newValue &&
      isCallActive.value &&
      providerFeatureManager.isFeatureAvailable(
        currentProvider.value,
        telephonyFeatures.callRecording,
      )
    ) {
      isRecording.value = true;
      handleRecording();
    }
  },
);

onMounted(() => {
  eventBus.on("call-open", handleCallOpen);
});

onUnmounted(() => {
  eventBus.off("call-open", handleCallOpen);
  resetAllStates();
});
</script>

<style scoped>
.call_action_container {
  border: 1px solid transparent;
  background: var(--white-opacity-4);
  transition: all 0.4s ease-in-out;
}

.call_action_container .action-icon {
  opacity: 0.7;
  transition: opacity 0.4s ease-in-out;
}

.call_action_container:hover .action-icon {
  opacity: 1;
}

.call_action_container:hover {
  background: var(--white-opacity-8);
}

.call_action_container.variant1 {
  background: var(--white-opacity-60);
}

.call_action_container.variant1 .action-icon {
  opacity: 1;
}

.call_action_container.variant2 {
  border: 1px solid var(--record-wrapper-border-color);
  background: var(--record-wrapper-bg-color);
}

.call_action_container.variant2 .action-icon {
  opacity: 1;
}

.call_action_container.disabled {
  cursor: not-allowed;
  pointer-events: none;
  background: var(--white-opacity-2);
}

.call_action_container.disabled .action-icon {
  opacity: 0.4;
  filter: grayscale(100%);
}

.call_action_container:not(.disabled):hover {
  transform: scale(1.05);
}
</style>
